/* eventRegisterMixin.js */

"use strict";

/**
 * @typedef {EventEmitter|HTMLElement|Window} Emitter
 *
 *
 * @class eventRegisterMixin
 * @classdesc mixin class to provide methods for register and unregister events
 */
function eventRegisterMixin(SuperClass = class {}) {
    return class extends SuperClass {
        /**
         *
         * @memberof eventRegisterMixin.prototype
         * @param {Emitter} emitter - EventEmitter class instance or DOMElement
         * @param {string} event - event name
         * @param {function} handler - event handler function
         * @param {?"on"|"once"} [type=on]
         * @param {boolean} [DOMEvent=false]
         * @return {eventRegisterMixin}
         * @private
         */
        _registerListener(emitter, event, handler, type = "on", DOMEvent = false) {
            /**
             * @type {{handler: function, event: string, emitter: (EventEmitter|HTMLElement), type: ("on"|"once"), DOMEvent: boolean}[]}
             * @private
             */
            this._listeners = this._listeners || [];

            if (DOMEvent) {
                emitter.addEventListener(event, handler);
            } else if (type === "on") {
                emitter = emitter.on(event, handler);
            } else if (type === "once") {
                emitter = emitter.once(event, handler);
            }
            this._listeners = this._listeners.concat({
                emitter,
                event,
                handler,
                DOMEvent
            });
            return this;
        }

        /**
         * @memberof eventRegisterMixin.prototype
         * @param {Emitter} emitter - EventEmitter class instance or DOMElement
         * @param {string} event
         * @param {function} handler
         * @return {eventRegisterMixin}
         * @private
         */

        _registerOnListener(emitter, event, handler) {
            return this._registerListener(emitter, event, handler, "on");
        }

        /**
         * @memberof eventRegisterMixin.prototype
         * @param {Emitter} [emitter] - EventEmitter class instance or DOMElement
         * @param {string} [event] - event name
         * @return {eventRegisterMixin}
         * @private
         */
        _unregisterAllListeners(emitter, event) {
            this._listeners = (this._listeners || []).reduce(
                (
                    result,
                    { emitter: _emitter, event: _event, handler: _handler, DOMEvent: _DOMEvent }
                ) => {
                    const toBeRemoved =
                        (emitter ? _emitter === emitter : true) &&
                        (event ? _event === event : true);

                    if (toBeRemoved) {
                        const removeEventCall = _DOMEvent
                            ? _emitter.removeEventListener
                            : _emitter.removeListener;
                        removeEventCall.call(_emitter, _event, _handler);
                    }
                    return toBeRemoved
                        ? result
                        : result.concat({ emitter: _emitter, event: _event, handler: _handler });
                },
                []
            );
            return this;
        }
    };
}

module.exports = eventRegisterMixin;
